import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight, Zap, ShieldCheck, Smartphone, Wallet, Star,
    CheckCircle2, Sun, Moon, MessageSquareQuote
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from "../components/ui/accordion";
import { useTheme } from "../context/ThemeContext";
import { api, formatIDR } from "../lib/api";
import { fetchCatalog } from "../lib/transaction";
import HeroIllustration from "../components/illustrations/HeroIllustration";
import PaymentIllustration from "../components/illustrations/PaymentIllustration";
import QRISIllustration from "../components/illustrations/QRISIllustration";

const FALLBACK_PRODUCTS = [
    { id: "fallback-pulsa-data", icon: "📱", name: "Pulsa & Data", category: "Produk populer" },
    { id: "fallback-token-pln", icon: "⚡", name: "Token PLN", category: "Produk populer" },
    { id: "fallback-bpjs", icon: "🏥", name: "BPJS", category: "Produk populer" },
    { id: "fallback-game", icon: "🎮", name: "Voucher Game", category: "Produk populer" },
    { id: "fallback-ewallet", icon: "💳", name: "E-Wallet", category: "Produk populer" },
    { id: "fallback-transfer", icon: "🏦", name: "Transfer Bank", category: "Produk populer" },
    { id: "fallback-pdam", icon: "💧", name: "PDAM", category: "Produk populer" },
    { id: "fallback-tagihan-pln", icon: "🧾", name: "Tagihan PLN", category: "Produk populer" },
];

const PRODUCT_ICON_RULES = [
    { icon: "📱", match: ["pulsa", "data", "internet", "gb", "mb", "telpon", "telepon", "sms", "masa aktif"] },
    { icon: "⚡", match: ["pln", "token", "listrik"] },
    { icon: "🏥", match: ["bpjs"] },
    { icon: "🎮", match: ["game", "games", "mobile legends", "free fire", "pubg"] },
    { icon: "💳", match: ["e-money", "emoney", "e-wallet", "ewallet", "dana", "ovo", "gopay", "go pay", "shopee pay", "shopeepay"] },
    { icon: "🏦", match: ["transfer", "bank"] },
    { icon: "💧", match: ["pdam", "air"] },
    { icon: "📺", match: ["tv"] },
    { icon: "🔥", match: ["gas"] },
    { icon: "🎟️", match: ["voucher", "aktivasi"] },
];

function productText(product) {
    return [
        product.category,
        product.type,
        product.brand,
        product.name,
    ].filter(Boolean).join(" ").toLowerCase();
}

function pickProductIcon(product) {
    const text = productText(product);
    return PRODUCT_ICON_RULES.find((entry) => entry.match.some((keyword) => text.includes(keyword)))?.icon || "🛒";
}

function normalizeCatalogProducts(items) {
    return items.map((product) => {
        const price = Number(product.sellPrice);
        const brand = product.metadata?.brand || product.metadata?.operator || "";
        return {
            id: product.id,
            name: product.name,
            category: product.category || "",
            brand,
            type: product.type,
            price: Number.isSafeInteger(price) && price > 0 ? price : null,
            icon: pickProductIcon({ name: product.name, category: product.category, brand, type: product.type }),
        };
    });
}

export default function LandingPage() {
    const navigate = useNavigate();
    const { theme, toggle } = useTheme();
    const [simAmount, setSimAmount] = useState(50);
    const [catalogProducts, setCatalogProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(true);
    const [catalogError, setCatalogError] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        fetchCatalog(api, null, controller.signal).then((items) => {
            if (!controller.signal.aborted) setCatalogProducts(normalizeCatalogProducts(items));
        }).catch(() => {
            if (!controller.signal.aborted) {
                setCatalogProducts([]);
                setCatalogError(true);
            }
        }).finally(() => {
            if (!controller.signal.aborted) setProductsLoading(false);
        });
        return () => controller.abort();
    }, []);

    const features = [
        { icon: Zap, title: "Transaksi <3 detik", desc: "Input nomor, pilih nominal, bayar. Selesai." },
        { icon: ShieldCheck, title: "Aman & Terpercaya", desc: "Sistem enkripsi & monitoring 24/7." },
        { icon: Smartphone, title: "Mobile First", desc: "Didesain untuk 1 tangan, di mana saja." },
        { icon: Wallet, title: "Komisi Langsung", desc: "Profit warung masuk saldo otomatis." },
    ];

    const products = useMemo(() => (
        catalogProducts.length > 0 ? catalogProducts : FALLBACK_PRODUCTS
    ), [catalogProducts]);

    const testimonials = [
        { name: "Pak Budi", warung: "Warung Sembako Berkah, Surabaya", text: "Sekarang pelanggan saya bisa bayar listrik, beli pulsa, BPJS — semua satu aplikasi. Profit per bulan naik 2x lipat.", rating: 5 },
        { name: "Ibu Sari", warung: "Konter Sari Cell, Bandung", text: "Tampilannya jelas, tombol besar, anak SMP saya saja bisa pakai. Setor saldo via QRIS langsung masuk.", rating: 5 },
        { name: "Pak Anto", warung: "Toko Anto Jaya, Bekasi", text: "Saya pakai WARUNGPAY 6 bulan. Belum pernah trouble. Tim CS responsif banget.", rating: 5 },
    ];

    const faqs = [
        { q: "Apa itu WARUNGPAY?", a: "WARUNGPAY adalah platform PPOB modern khusus untuk mitra warung di Indonesia. Anda bisa jual pulsa, paket data, token PLN, BPJS, voucher game, transfer bank, dan banyak lagi dari 1 aplikasi." },
        { q: "Berapa modal awal untuk jadi mitra?", a: "Daftar GRATIS. Anda hanya perlu deposit saldo minimal Rp 50.000 untuk mulai transaksi. Sudah dapat bonus saldo Rp 50.000 saat register." },
        { q: "Bagaimana cara dapat keuntungan?", a: "Setiap transaksi pelanggan ke warung Anda, fee jualan otomatis masuk saldo sebagai profit. Misal jual pulsa 10rb harga 11.500 → Anda untung 1.500." },
        { q: "Apakah aman?", a: "100% aman. Setiap transaksi terenkripsi, ada nomor referensi, struk digital, dan riwayat lengkap untuk audit." },
        { q: "Apakah bisa cetak struk?", a: "Bisa! Setiap transaksi sukses bisa langsung dicetak sebagai bukti pembayaran ke pelanggan." },
    ];

    const loyaltyStatuses = [
        { icon: "🛒", name: "Warung Jagoan", target: 1000000, bonus: "0,25%", philosophy: "Mitra yang baru bergabung dan mulai membangun transaksi bersama P4Yu." },
        { icon: "💪", name: "Warung Andal", target: 2000000, bonus: "0,50%", philosophy: "Mitra yang aktif bertransaksi secara konsisten dan mulai dipercaya pelanggan." },
        { icon: "⭐", name: "Warung Langganan", target: 3000000, bonus: "0,75%", philosophy: "Warung dengan transaksi yang stabil dan telah memiliki pelanggan tetap." },
        { icon: "🏪", name: "Warung Favorit", target: 4000000, bonus: "1,00%", philosophy: "Warung yang menjadi pilihan utama masyarakat di lingkungan sekitarnya." },
        { icon: "👑", name: "Warung Juara", target: 5000000, bonus: "1,25%", philosophy: "Status tertinggi bagi mitra P4Yu dengan performa transaksi terbaik dan konsisten." },
    ];

    const accountTypes = [
        {
            title: "Member",
            audience: "Ditujukan untuk pengguna pribadi.",
            benefits: [
                "Harga member.",
                "Dapat melakukan seluruh transaksi.",
                "Tidak mengikuti Program Bonus Warung.",
                "Tidak memiliki Status Warung.",
            ],
        },
        {
            title: "Keluarga P4Yu",
            audience: "Ditujukan untuk Warung Madura, Toko Kelontong, Counter Pulsa, UMKM, dan Mitra Penjualan.",
            benefits: [
                "Harga lebih murah dibanding Member.",
                "Mengikuti Program Bonus Warung.",
                "Mendapat Bonus Warung setiap bulan sesuai omzet.",
                "Memiliki Status Warung.",
                "Mendapatkan promo dan penawaran khusus.",
            ],
        },
    ];

    const dailyTx = simAmount;
    const profitPerTx = 1500;
    const monthlyProfit = dailyTx * profitPerTx * 30;

    return (
        <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
            {/* Top Bar */}
            <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
                <Link
    to="/"
    className="flex items-center gap-3"
    data-testid="brand-logo"
>
    <div className="h-12 w-12 flex-shrink-0 flex items-center justify-center">
        <img
            src="/images/LG_P4YU.png"
            alt="WarungPay Logo"
            className="w-full h-full object-contain"
        />
    </div>

    <div>
        <div className="font-heading font-extrabold text-lg leading-none tracking-tight">
            WARUNGPAY
        </div>
        <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
            PPOB Indonesia
        </div>
    </div>
</Link>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme" data-testid="theme-toggle-landing">
                            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </Button>
                        <Button data-testid="login-link" variant="ghost" onClick={() => navigate("/login")} className="hidden sm:inline-flex font-semibold">Masuk</Button>
                        <Button data-testid="hero-cta-daftar" onClick={() => navigate("/register")} className="bg-brand-green hover:bg-brand-greenDark text-white rounded-xl font-semibold">
                            Daftar Mitra
                        </Button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-20 grid lg:grid-cols-2 gap-10 items-center">
                <div className="absolute inset-0 -z-10 pointer-events-none">
                    <div className="absolute top-20 -left-20 h-80 w-80 rounded-full bg-brand-green/20 blur-3xl" />
                    <div className="absolute top-40 right-10 h-72 w-72 rounded-full bg-brand-deep/20 dark:bg-blue-500/10 blur-3xl" />
                </div>
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-green/10 text-brand-green text-xs font-semibold mb-5">
                        <span className="h-2 w-2 rounded-full bg-brand-green animate-pulse" />
                        Platform PPOB #1 untuk Warung Indonesia
                    </div>
                    <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        Warung Anda,<br />
                        <span className="text-brand-deep dark:text-white">jadi </span>
                        <span className="relative inline-block">
                            <span className="text-brand-green">mesin cuan.</span>
                            <span className="absolute -bottom-1 left-0 right-0 h-2 bg-brand-green/20 rounded-full -z-10" />
                        </span>
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                        Jual pulsa, paket data, token PLN, BPJS, voucher game, sampai transfer bank — semua dari satu dashboard yang ringan, cepat, dan dipercaya ribuan mitra warung.
                    </p>
                    <div className="mt-7 flex flex-col sm:flex-row gap-3">
                        <Button
                            data-testid="hero-daftar-btn"
                            onClick={() => navigate("/register")}
                            className="bg-brand-green hover:bg-brand-greenDark text-white rounded-xl h-14 px-7 text-base font-semibold shadow-lg shadow-brand-green/30"
                        >
                            Daftar Mitra Gratis <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                        <Button
                            data-testid="hero-login-btn"
                            onClick={() => navigate("/login")}
                            variant="outline"
                            className="rounded-xl h-14 px-7 text-base font-semibold border-2"
                        >
                            Sudah Punya Akun?
                        </Button>
                    </div>
                    <div className="mt-7 flex items-center gap-6 text-sm">
                        <div className="flex -space-x-2">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="h-9 w-9 rounded-full bg-gradient-to-br from-brand-green to-brand-deep border-2 border-background" />
                            ))}
                        </div>
                        <div>
                            <div className="font-heading font-bold">12.000+ Mitra Warung</div>
                            <div className="text-muted-foreground text-xs flex items-center gap-1">
                                <Star className="h-3 w-3 fill-amber-500 text-amber-500" /> 4.9/5 rating
                            </div>
                        </div>
                    </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 }} className="relative">
                    <HeroIllustration />
                    {/* Floating stat */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
                        className="absolute -left-4 top-10 bg-white dark:bg-card rounded-2xl p-3 shadow-xl border border-border hidden sm:block"
                    >
                        <div className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                                <Zap className="h-4 w-4 text-brand-green" />
                            </div>
                            <div>
                                <div className="text-[10px] text-muted-foreground">Transaksi Sukses</div>
                                <div className="font-heading font-bold text-sm">2.4 detik</div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </section>

            {/* Products */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
                <div className="text-center mb-8">
                    <div className="text-xs uppercase tracking-widest text-brand-green font-semibold">Produk Lengkap</div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2 tracking-tight">Semua kebutuhan pelanggan, satu aplikasi.</h2>
                    <p className="text-sm text-muted-foreground mt-3">
                        Katalog internal hanya sebagai referensi; harga final dan ketersediaan provider dapat berubah dan dikonfirmasi sebelum transaksi.
                    </p>
                    {catalogError && (
                        <p role="status" className="text-sm text-muted-foreground mt-2">
                            Katalog internal belum dapat dimuat. Produk dan harga akan divalidasi server setelah Anda memilih layanan.
                        </p>
                    )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                    {productsLoading ? (
                        Array.from({ length: 12 }).map((_, i) => (
                            <div key={i} className="rounded-2xl bg-card border border-border p-4 min-h-[136px]">
                                <div className="flex items-start gap-3">
                                    <div className="h-10 w-10 rounded-xl bg-muted animate-pulse" />
                                    <div className="flex-1">
                                        <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
                                        <div className="h-3 w-1/2 rounded bg-muted animate-pulse mt-2" />
                                    </div>
                                </div>
                                <div className="h-5 w-24 rounded bg-muted animate-pulse mt-5" />
                            </div>
                        ))
                    ) : products.map((p) => (
                        <div key={p.id} className="rounded-2xl bg-card border border-border p-4 min-h-[148px] flex flex-col justify-between hover:border-brand-green/50 hover:shadow-md transition">
                            <div className="flex items-start gap-3">
                                <div className="h-10 w-10 rounded-xl bg-brand-green/10 flex items-center justify-center text-2xl flex-shrink-0">
                                    {p.icon}
                                </div>
                                <div className="min-w-0 text-left">
                                    <div className="font-heading font-semibold text-sm leading-tight break-words">{p.name}</div>
                                    <div className="text-[11px] text-muted-foreground leading-snug mt-1 break-words">
                                        {[p.category, p.brand, p.type].filter(Boolean).join(" • ")}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-end justify-between gap-3 mt-4">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Harga</div>
                                    <div className="font-heading font-bold text-sm text-brand-green">
                                        {p.price == null ? "Lihat layanan" : formatIDR(p.price)}
                                    </div>
                                </div>
                                {p.code && (
                                    <div className="rounded-full bg-muted px-2 py-1 text-[10px] font-semibold text-muted-foreground max-w-[96px] truncate">
                                        {p.code}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Features */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-16 grid lg:grid-cols-2 gap-10 items-center">
                <PaymentIllustration />
                <div>
                    <div className="text-xs uppercase tracking-widest text-brand-green font-semibold">Keunggulan</div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2 tracking-tight">Dibangun untuk warung yang gak mau ribet.</h2>
                    <p className="text-muted-foreground mt-3">Setiap detik berarti. Dashboard WARUNGPAY didesain agar 1 tangan bisa transaksi dalam beberapa detik.</p>
                    <div className="grid sm:grid-cols-2 gap-4 mt-7">
                        {features.map((f) => {
                            const Icon = f.icon;
                            return (
                                <div key={f.title} className="rounded-2xl p-4 bg-card border border-border">
                                    <div className="h-10 w-10 rounded-xl bg-brand-green/10 flex items-center justify-center">
                                        <Icon className="h-5 w-5 text-brand-green" />
                                    </div>
                                    <div className="font-heading font-semibold text-base mt-3">{f.title}</div>
                                    <div className="text-sm text-muted-foreground mt-1">{f.desc}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Simulasi keuntungan */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
                <div className="rounded-3xl bg-gradient-to-br from-brand-deep to-[#0c2752] dark:from-brand-deeper dark:to-brand-deep text-white p-7 sm:p-12 relative overflow-hidden">
                    <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-green/20 blur-3xl" />
                    <div className="relative grid lg:grid-cols-2 gap-8 items-center">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-brand-greenGlow font-semibold">Simulasi Keuntungan</div>
                            <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2 tracking-tight">Hitung potensi cuan warung Anda.</h2>
                            <p className="text-white/70 mt-3 max-w-md">Geser slider untuk lihat estimasi profit harian & bulanan berdasarkan rata-rata 1.500/transaksi.</p>
                            <div className="mt-7">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm text-white/80">Transaksi per hari</label>
                                    <div className="font-heading font-bold text-xl text-brand-greenGlow">{dailyTx}</div>
                                </div>
                                <input
                                    data-testid="profit-slider"
                                    type="range"
                                    min="10"
                                    max="200"
                                    value={simAmount}
                                    onChange={(e) => setSimAmount(parseInt(e.target.value))}
                                    className="w-full h-2 rounded-full appearance-none bg-white/10 accent-brand-green cursor-pointer"
                                />
                                <div className="flex justify-between text-xs text-white/40 mt-1">
                                    <span>10</span><span>100</span><span>200</span>
                                </div>
                            </div>
                        </div>
                        <div className="grid gap-4">
                            <div className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 p-5">
                                <div className="text-xs uppercase tracking-widest text-white/60">Profit / Hari</div>
                                <div className="font-heading font-extrabold text-3xl mt-2 text-white">Rp {(dailyTx * profitPerTx).toLocaleString("id-ID")}</div>
                            </div>
                            <div className="rounded-2xl bg-brand-green text-brand-deeper p-5 shadow-xl shadow-brand-green/30">
                                <div className="text-xs uppercase tracking-widest text-brand-deeper/70">Profit / Bulan</div>
                                <div className="font-heading font-extrabold text-4xl mt-2">Rp {monthlyProfit.toLocaleString("id-ID")}</div>
                                <div className="text-xs mt-2 text-brand-deeper/80">*Estimasi rata-rata Rp 1.500/transaksi</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bonus Warung loyalty */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
                <div className="rounded-3xl border border-border bg-card p-6 sm:p-10">
                    <div className="text-center max-w-3xl mx-auto">
                        <div className="text-xs uppercase tracking-widest text-brand-green font-semibold">P4Yu Loyalty Program</div>
                        <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2 tracking-tight">Bonus Warung</h2>
                        <p className="mt-3 text-base sm:text-lg text-brand-deep dark:text-brand-green font-semibold">Semakin Ramai Transaksi, Semakin Besar Bonusnya.</p>
                        <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
                            Program loyalitas eksklusif untuk Keluarga P4Yu sebagai bentuk apresiasi bagi mitra yang aktif bertransaksi setiap bulan.
                            Semakin tinggi omzet transaksi, semakin tinggi Status Warung dan semakin besar Bonus Warung yang diterima.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mt-8">
                        {accountTypes.map((type) => (
                            <div key={type.title} className="rounded-2xl border border-border bg-background p-5">
                                <h3 className="font-heading font-bold text-xl">{type.title}</h3>
                                <p className="text-sm text-muted-foreground mt-2">{type.audience}</p>
                                <ul className="mt-4 space-y-2 text-sm">
                                    {type.benefits.map((benefit) => (
                                        <li key={benefit} className="flex items-start gap-2">
                                            <CheckCircle2 className="h-4 w-4 text-brand-green mt-0.5 flex-shrink-0" />
                                            <span>{benefit}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8">
                        <h3 className="font-heading font-bold text-2xl tracking-tight">Status Warung & Bonus</h3>
                        <div className="mt-4 overflow-x-auto rounded-2xl border border-border">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/60">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-semibold">Status Warung</th>
                                        <th className="text-left px-4 py-3 font-semibold">Target Omzet Bulanan</th>
                                        <th className="text-left px-4 py-3 font-semibold">Bonus Warung</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loyaltyStatuses.map((status) => (
                                        <tr key={status.name} className="border-t border-border">
                                            <td className="px-4 py-3 font-semibold">{status.icon} {status.name}</td>
                                            <td className="px-4 py-3">{formatIDR(status.target)}</td>
                                            <td className="px-4 py-3 text-brand-green font-semibold">{status.bonus}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="mt-8">
                        <h3 className="font-heading font-bold text-2xl tracking-tight">Filosofi Status Warung</h3>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                            {loyaltyStatuses.map((status) => (
                                <div key={`philosophy-${status.name}`} className="rounded-2xl border border-border bg-background p-4">
                                    <div className="font-heading font-semibold text-base">{status.icon} {status.name}</div>
                                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{status.philosophy}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 grid lg:grid-cols-2 gap-4">
                        <div className="rounded-2xl border border-brand-green/30 bg-brand-green/5 p-5">
                            <h3 className="font-heading font-bold text-xl">Cara Kerja Program</h3>
                            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Setiap transaksi berhasil otomatis menambah omzet bulanan Anda.</p>
                            <div className="mt-4 rounded-xl bg-background border border-border p-4 text-sm">
                                <div className="flex justify-between gap-4"><span className="text-muted-foreground">Status saat ini</span><span className="font-semibold">⭐ Warung Langganan</span></div>
                                <div className="flex justify-between gap-4 mt-2"><span className="text-muted-foreground">Target berikutnya</span><span className="font-semibold">🏪 Warung Favorit</span></div>
                                <div className="flex justify-between gap-4 mt-2"><span className="text-muted-foreground">Progress</span><span className="font-semibold">Rp3.250.000 / Rp4.000.000</span></div>
                                <p className="mt-3 text-brand-deep dark:text-brand-green font-semibold">Sisa Rp750.000 lagi menuju Warung Favorit.</p>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-border bg-background p-5">
                            <h3 className="font-heading font-bold text-xl">Periode & Reset Progress</h3>
                            <p className="text-sm text-muted-foreground mt-2">Perhitungan omzet berlaku setiap tanggal 1 hingga tanggal terakhir tiap bulan.</p>
                            <ul className="mt-4 space-y-2 text-sm">
                                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-brand-green mt-0.5" />Progress omzet reset ke Rp0 di awal bulan berikutnya.</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-brand-green mt-0.5" />Perhitungan omzet dimulai kembali dari nol.</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-brand-green mt-0.5" />Status Warung yang sudah dimiliki tetap dipertahankan.</li>
                            </ul>
                        </div>
                    </div>

                    <div className="mt-4 grid lg:grid-cols-2 gap-4">
                        <div className="rounded-2xl border border-border bg-background p-5">
                            <h3 className="font-heading font-bold text-xl">Kenaikan & Penurunan Status</h3>
                            <ul className="mt-3 space-y-2 text-sm">
                                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-brand-green mt-0.5" />Status naik otomatis jika target omzet bulanan tercapai.</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-brand-green mt-0.5" />Status tidak turun hanya karena gagal capai target bulanan.</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-brand-green mt-0.5" />Status turun 1 level bila tidak ada transaksi selama 3 bulan berturut-turut.</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-brand-green mt-0.5" />Batas minimum status adalah Warung Jagoan.</li>
                            </ul>
                        </div>

                        <div className="rounded-2xl border border-border bg-background p-5">
                            <h3 className="font-heading font-bold text-xl">Perhitungan Bonus Warung</h3>
                            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Bonus dihitung dari total omzet transaksi bulanan sesuai status yang dimiliki, lalu otomatis ditambahkan ke saldo P4Yu pada awal bulan berikutnya.</p>
                            <div className="mt-4 rounded-xl border border-border bg-muted/30 p-4 text-sm">
                                <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className="font-semibold">🏪 Warung Favorit</span></div>
                                <div className="flex justify-between mt-2"><span className="text-muted-foreground">Omzet Juli</span><span className="font-semibold">Rp4.850.000</span></div>
                                <div className="flex justify-between mt-2"><span className="text-muted-foreground">Bonus</span><span className="font-semibold">1,00%</span></div>
                                <div className="flex justify-between mt-2 border-t border-border pt-2"><span className="text-muted-foreground">Bonus diterima</span><span className="font-bold text-brand-green">Rp48.500</span></div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 rounded-2xl bg-gradient-to-r from-brand-green/15 to-brand-deep/10 border border-brand-green/20 p-5 sm:p-6 text-center">
                        <h3 className="font-heading font-bold text-2xl tracking-tight">Komitmen P4Yu</h3>
                        <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed max-w-3xl mx-auto">
                            Di P4Yu, kami percaya pertumbuhan bisnis dibangun bersama. Setiap transaksi bukan hanya memberi keuntungan dari penjualan,
                            tetapi juga membawa mitra semakin dekat menuju Status Warung lebih tinggi dan Bonus Warung lebih besar.
                        </p>
                        <p className="mt-4 font-semibold text-brand-deep dark:text-brand-green">Semakin aktif bertransaksi, semakin besar apresiasi yang kami berikan.</p>
                        <p className="mt-4 text-sm font-medium">P4Yu - Pay For U - Teman Transaksi untuk Semua Warung.</p>
                    </div>
                </div>
            </section>

            {/* QRIS section */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-16 grid lg:grid-cols-2 gap-10 items-center">
                <div>
                    <div className="text-xs uppercase tracking-widest text-brand-green font-semibold">QRIS & Multi-Mitra</div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2 tracking-tight">Terima pembayaran QRIS, kelola banyak warung.</h2>
                    <p className="text-muted-foreground mt-3 max-w-lg">Generate QRIS dinamis untuk setiap transaksi. Kelola multi-cabang dari 1 akun pusat.</p>
                    <ul className="mt-6 space-y-3">
                        {["Top up saldo via QRIS langsung", "Print struk fisik & digital", "Auto-reconcile harian", "Multi outlet dashboard"].map((t) => (
                            <li key={t} className="flex items-start gap-3">
                                <CheckCircle2 className="h-5 w-5 text-brand-green flex-shrink-0 mt-0.5" />
                                <span className="text-sm">{t}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="relative">
                    <QRISIllustration />
                </div>
            </section>

            {/* Testimonials */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
                <div className="text-center mb-10">
                    <div className="text-xs uppercase tracking-widest text-brand-green font-semibold">Testimoni Mitra</div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2 tracking-tight">Mereka sudah cuan lebih dulu.</h2>
                </div>
                <div className="grid md:grid-cols-3 gap-5">
                    {testimonials.map((t, i) => (
                        <motion.div
                            key={t.name}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="rounded-2xl bg-card border border-border p-6 relative"
                        >
                            <MessageSquareQuote className="absolute -top-3 left-5 h-7 w-7 text-brand-green bg-background rounded-full p-1" />
                            <div className="flex gap-0.5 mb-3">
                                {Array.from({ length: t.rating }).map((_, idx) => (
                                    <Star key={idx} className="h-4 w-4 fill-amber-500 text-amber-500" />
                                ))}
                            </div>
                            <p className="text-sm leading-relaxed">{t.text}</p>
                            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-border">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-brand-green to-brand-deep" />
                                <div>
                                    <div className="font-heading font-semibold text-sm">{t.name}</div>
                                    <div className="text-xs text-muted-foreground">{t.warung}</div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* FAQ */}
            <section className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
                <div className="text-center mb-8">
                    <div className="text-xs uppercase tracking-widest text-brand-green font-semibold">FAQ</div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2 tracking-tight">Pertanyaan paling sering ditanya.</h2>
                </div>
                <Accordion type="single" collapsible className="space-y-2">
                    {faqs.map((f, i) => (
                        <AccordionItem key={i} value={`item-${i}`} className="rounded-2xl border border-border px-5 bg-card">
                            <AccordionTrigger className="font-heading font-semibold text-left text-base" data-testid={`faq-trigger-${i}`}>
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                                {f.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </section>

            {/* CTA */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
                <div className="rounded-3xl bg-brand-green text-brand-deeper p-8 sm:p-12 text-center relative overflow-hidden">
                    <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-white/20 blur-3xl" />
                    <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">Siap jadi mitra WARUNGPAY?</h2>
                    <p className="mt-3 text-brand-deeper/80 max-w-lg mx-auto">Daftar gratis, dapat bonus saldo Rp 50.000, dan mulai transaksi dalam 1 menit.</p>
                    <Button
                        data-testid="bottom-cta-daftar"
                        onClick={() => navigate("/register")}
                        className="mt-6 bg-brand-deep hover:bg-brand-deeper text-white rounded-xl h-14 px-8 text-base font-semibold"
                    >
                        Daftar Mitra Sekarang <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-border mt-10">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-3 gap-8">
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <div className="h-9 w-9 rounded-xl bg-brand-deep dark:bg-brand-green flex items-center justify-center">
                                <span className="font-heading font-bold text-white dark:text-brand-deeper text-lg">W</span>
                            </div>
                            <div className="font-heading font-extrabold text-lg">WARUNGPAY</div>
                        </div>
                        <p className="text-sm text-muted-foreground">Platform PPOB modern untuk mitra warung Indonesia. Cepat, aman, terpercaya.</p>
                    </div>
                    <div>
                        <div className="font-heading font-semibold text-sm mb-3">Produk</div>
                        <ul className="text-sm text-muted-foreground space-y-2">
                            <li>Pulsa & Data</li>
                            <li>Token PLN & BPJS</li>
                            <li>E-Wallet & Transfer</li>
                            <li>Voucher Game</li>
                        </ul>
                    </div>
                    <div>
                        <div className="font-heading font-semibold text-sm mb-3">Perusahaan</div>
                        <ul className="text-sm text-muted-foreground space-y-2">
                            <li>Tentang Kami</li>
                            <li>Karir</li>
                            <li>Hubungi CS</li>
                            <li>Syarat & Ketentuan</li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-border py-5 text-center text-xs text-muted-foreground">
                    © 2026 WARUNGPAY. Dibuat dengan ❤️ untuk warung Indonesia.
                </div>
            </footer>
        </div>
    );
}
